/* Lux local instant-search engine.
   Replaces per-keystroke server calls with one small per-locale index
   (/collections/all?view=lux-search-index, +&page=N) searched in the browser:
   real typo tolerance, greeklish, our own ranking (a bed query returns beds,
   not headboards), and Hebrew type-ahead (Shopify's predictive API rejects he).
   Tuning constants (authored, validated by tests on the real index):
   availability nudge 0.9, relative score floor 0.55.
   Pure logic is DOM-free so it can be unit-tested in Node.
*/
(function (root) {
  "use strict";

  // -- normalization ---------------------------------------------------------

  var GREEKLISH_DIGRAPHS = [
    [/th/g, "θ"], [/ch/g, "χ"], [/ps/g, "ψ"], [/ks/g, "ξ"],
  ];
  var GREEKLISH_SINGLES = {
    a: "α", b: "μπ", c: "κ", d: "δ", e: "ε", f: "φ", g: "γ",
    h: "η", i: "ι", k: "κ", l: "λ", m: "μ", n: "ν", o: "ο",
    p: "π", r: "ρ", s: "σ", t: "τ", u: "υ", v: "β", w: "ω",
    x: "χ", y: "υ", z: "ζ", "8": "θ",
  };

  function norm(s) {
    return String(s)
      .toLowerCase()
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .replace(/ς/g, "σ");
  }

  function splitWords(s) {
    return norm(s).split(/[^\p{L}\p{N}]+/u).filter(Boolean);
  }

  // Matching-only vowel folding (Greek sound-alikes + latin y): ω/ο, η/ι/υ all
  // collapse, so "stromata" (ο) finds στρώμα (ω) and "polithrona" finds
  // πολυθρόνα. Vowel pairs also accept phonetic spellings (epipla / έπιπλα).
  // NEVER display or submit folded text — real spelling comes back
  // through the vocabulary.
  function fold(s) {
    return s.replace(/αι/g, "ε").replace(/ει|οι|υι/g, "ι")
      .replace(/ω/g, "ο").replace(/[ηυ]/g, "ι").replace(/y/g, "i");
  }

  // Display/submit form: restore the final sigma the normalizer flattened.
  function displayTerms(s) {
    return s.split(" ").map(function (w) { return w.replace(/σ$/u, "ς"); }).join(" ");
  }

  function greeklish(token, thAsTauEta) {
    if (!/[a-z]/.test(token) || /[α-ω]/.test(token)) return null;
    var out = token;
    // "th" is ambiguous: θάλασσα (th=θ) vs τηλεόραση (th=τη). The caller asks
    // for both readings and matches either.
    if (thAsTauEta) out = out.replace(/th/g, "τη");
    GREEKLISH_DIGRAPHS.forEach(function (d) { out = out.replace(d[0], d[1]); });
    out = out.replace(/[a-z8]/g, function (ch) { return GREEKLISH_SINGLES[ch] || ch; });
    if (!/[α-ω]/.test(out) || out === token) return null;
    return out;
  }

  // Greek → greeklish projection (for predicting in the alphabet the shopper
  // is typing). Input must already be norm()ed. ου→ou first, υ alone → y.
  var LATIN_SINGLES = {
    "α": "a", "β": "v", "γ": "g", "δ": "d", "ε": "e", "ζ": "z", "η": "i",
    "θ": "th", "ι": "i", "κ": "k", "λ": "l", "μ": "m", "ν": "n", "ξ": "ks",
    "ο": "o", "π": "p", "ρ": "r", "σ": "s", "τ": "t", "υ": "y", "φ": "f",
    "χ": "x", "ψ": "ps", "ω": "o",
  };
  function greekToLatin(s) {
    var out = "";
    for (var i = 0; i < s.length; i++) {
      var ch = s[i];
      if (ch === "ο" && s[i + 1] === "υ") { out += "ou"; i++; continue; }
      out += LATIN_SINGLES[ch] || ch;
    }
    return out;
  }

  // Bounded Levenshtein: returns distance if <= max, else max+1. Early abandon per row.
  function editDist(a, b, max) {
    var la = a.length, lb = b.length;
    if (Math.abs(la - lb) > max) return max + 1;
    var prev = new Array(lb + 1), cur = new Array(lb + 1);
    for (var j = 0; j <= lb; j++) prev[j] = j;
    for (var i = 1; i <= la; i++) {
      cur[0] = i;
      var rowMin = cur[0];
      for (j = 1; j <= lb; j++) {
        var cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
        cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
        if (cur[j] < rowMin) rowMin = cur[j];
      }
      if (rowMin > max) return max + 1;
      var t = prev; prev = cur; cur = t;
    }
    return prev[lb] <= max ? prev[lb] : max + 1;
  }

  // -- scoring ---------------------------------------------------------------

  // kinds ordered by confidence; 'fuzzy' on the winning token means the query
  // was probably misspelled (drives the corrected-term note).
  var KIND = { exact: 2, prefix: 1.5, stem: 1, fuzzy: 0.8, sub: 0.4 };

  // Parent collections from the storefront navigation. Only use a destination
  // present in the current locale's index; never manufacture a collection URL.
  var CATEGORY_PARENTS = [
    ["κρεβατι", "krebatia"], ["καναπεσ", "kanapedes"],
    ["στρωμα", "stromata-ypnou"], ["πολυθρονα", "poluthrones-saloniou"],
    ["καρεκλα", "karekles-polithrones-stools"],
  ];

  function tokenVariants(tok) {
    var v = [fold(tok)];
    // Hebrew feminine singular/plural, including short words such as ספה/ספות.
    // Keep this script-specific so model names and other languages are untouched.
    if (/^[א-ת]{2,}ה$/.test(tok)) v.push(tok.slice(0, -1) + "ות");
    if (/^[א-ת]{2,}ות$/.test(tok)) v.push(tok.slice(0, -2) + "ה");
    var gr = greeklish(tok);
    if (gr) {
      gr = fold(gr);
      if (v.indexOf(gr) === -1) v.push(gr);
    }
    if (/th/.test(tok)) {
      var gr2 = greeklish(tok, true);
      if (gr2) {
        gr2 = fold(gr2);
        if (v.indexOf(gr2) === -1) v.push(gr2);
      }
    }
    // Both conventions are common: krevati / krebati, vivliothiki / bibliothiki.
    if (/b/.test(tok)) {
      var beta = greeklish(tok.replace(/b/g, "v"));
      if (beta && v.indexOf(fold(beta)) === -1) v.push(fold(beta));
    }
    // Mixed-script model codes: ΤΑ817L is commonly typed as TA817L. Do not
    // reinterpret the 8 in a number as theta, or the Latin suffix as Greek.
    if (/[a-z]+\d+[a-z]*/.test(tok)) {
      var code = tok.replace(/^[a-z]+(?=\d)/, function (prefix) { return greeklish(prefix) || prefix; });
      if (code !== tok) v.push(fold(code));
    }
    return v;
  }

  function sharedPrefixLen(a, b) {
    var n = Math.min(a.length, b.length), i = 0;
    while (i < n && a.charCodeAt(i) === b.charCodeAt(i)) i++;
    return i;
  }

  function bestWordScore(word, variants, weight) {
    var best = 0, kind = null;
    for (var i = 0; i < variants.length; i++) {
      var tv = variants[i];
      if (word === tv) { if (weight * KIND.exact > best) { best = weight * KIND.exact; kind = "exact"; } continue; }
      if (tv.length >= 2 && word.indexOf(tv) === 0) { if (weight * KIND.prefix > best) { best = weight * KIND.prefix; kind = "prefix"; } continue; }
      // Inflection stems: Greek καναπές/καναπέδες, κρεβάτι/κρεβατιού, Hebrew
      // construct state מיטה/מיטת. All-but-one shared = as good as a prefix hit.
      var sp = sharedPrefixLen(word, tv);
      var minLen = Math.min(word.length, tv.length);
      if (sp >= 3 && sp >= minLen - 1) { if (weight * KIND.prefix > best) { best = weight * KIND.prefix; kind = "prefix"; } continue; }
      if (sp >= 4 && sp >= minLen - 2) { if (weight * KIND.stem > best) { best = weight * KIND.stem; kind = "stem"; } continue; }
      if (tv.length >= 3 && word.length >= 4 && word.indexOf(tv) > 0) { if (weight * KIND.sub > best) { best = weight * KIND.sub; kind = "sub"; } }
    }
    return best ? { score: best, kind: kind } : null;
  }

  function fuzzyWordScore(word, variants, weight) {
    var best = 0;
    for (var i = 0; i < variants.length; i++) {
      var tv = variants[i];
      if (tv.length < 4) continue;
      var max = tv.length >= 7 ? 2 : 1;
      // compare against the word AND against its tv-length prefix (typo while
      // still typing an inflected form: "krevsti" vs "κρεβατιου")
      var d = Math.min(editDist(word, tv, max), editDist(word.slice(0, tv.length), tv, max));
      if (d <= max) {
        var s = weight * KIND.fuzzy / d;
        if (s > best) best = s;
      }
    }
    return best ? { score: best, kind: "fuzzy" } : null;
  }

  function scoreDoc(doc, tokens) {
    // Per token: sum the best hit of EACH field (type+title together beat
    // title-only, so a bed outranks a sofa-bed for a bed query), then AND
    // across tokens.
    var total = 0, kinds = [];
    for (var t = 0; t < tokens.length; t++) {
      var variants = tokens[t].variants;
      var tokenScore = 0, tokenKind = null;
      for (var f = 0; f < doc.fields.length; f++) {
        var field = doc.fields[f];
        var fieldBest = null;
        for (var w = 0; w < field.words.length; w++) {
          var hit = bestWordScore(field.words[w], variants, field.weight);
          if (hit && (!fieldBest || hit.score > fieldBest.score)) fieldBest = hit;
        }
        if (fieldBest) {
          tokenScore += fieldBest.score;
          if (!tokenKind || fieldBest.score > 0) tokenKind = tokenKind || fieldBest.kind;
        }
      }
      if (!tokenScore) {
        var fuzzyBest = null;
        for (f = 0; f < doc.fields.length; f++) {
          field = doc.fields[f];
          for (w = 0; w < field.words.length; w++) {
            var fhit = fuzzyWordScore(field.words[w], variants, field.weight);
            if (fhit && (!fuzzyBest || fhit.score > fuzzyBest.score)) fuzzyBest = fhit;
          }
        }
        if (fuzzyBest) { tokenScore = fuzzyBest.score; tokenKind = "fuzzy"; }
      }
      if (!tokenScore) return null; // AND semantics: every token must land somewhere
      total += tokenScore;
      kinds.push(tokenKind);
    }
    return { score: total, kinds: kinds };
  }

  function buildDocs(items, fieldSpecs) {
    return items.map(function (item) {
      return {
        item: item,
        fields: fieldSpecs.map(function (spec) {
          return { weight: spec[1], words: splitWords(item[spec[0]] || "").map(fold) };
        }),
      };
    });
  }

  function rank(docs, tokens, opts) {
    var scored = [];
    for (var i = 0; i < docs.length; i++) {
      var r = scoreDoc(docs[i], tokens);
      if (!r) continue;
      var score = r.score;
      if (opts.product) {
        var typeCoverage = Math.max(coverage(docs[i].fields[0].words, tokens),
          coverage(docs[i].fields[4].words, tokens));
        score *= 1 + typeCoverage;
      }
      if (opts.product) {
        var exactTitle = [docs[i].fields[1], docs[i].fields[3]].some(function (field) {
          return field.words.length === tokens.length && field.words.every(function (word, index) {
            return tokens[index].variants.indexOf(word) !== -1;
          });
        });
        if (exactTitle) score *= 4; // A named product outranks inconsistent types.
      }
      if (opts.collection) {
        // A whole-category match must beat a title that merely contains it:
        // Κρεβάτια before King Size Κρεβάτια or Καναπέδες κρεβάτια.
        score *= 1 + Math.max(coverage(docs[i].fields[0].words, tokens),
          coverage(docs[i].fields[1].words, tokens));
      }
      if (docs[i].item.av === false) score *= 0.9;
      scored.push({ item: docs[i].item, score: score, kinds: r.kinds, doc: docs[i] });
    }
    scored.sort(function (a, b) { return b.score - a.score; });
    if (!scored.length) return scored;
    var floor = Math.max(scored[0].score * (opts.rel || 0.55), opts.min || 4);
    return scored.filter(function (s) { return s.score >= floor; }).slice(0, opts.limit);
  }

  // -- engine ----------------------------------------------------------------

  function coverage(words, tokens) {
    return words.filter(function (word) {
      return tokens.some(function (token) {
        var hit = bestWordScore(word, token.variants, 1);
        return hit && hit.kind !== "sub";
      });
    }).length / Math.max(words.length, 1);
  }

  function catalogKey(item) { return item.id != null ? String(item.id) : item.u.split("?")[0].split("/").pop(); }

  function LuxSearchEngine(indexUrl, storage) {
    this.indexUrl = indexUrl;
    this.storage = storage || null;
    this.ready = false;
    this.failed = false;
    this.loading = null;
  }

  LuxSearchEngine.prototype.setData = function (data, greekData) {
    // Match the same real catalog item across locales; keep the selected locale's
    // title, URL, price and availability as the result shown to the shopper.
    if (greekData && data.locale !== "el") {
      ["products", "collections", "articles"].forEach(function (kind) {
        var aliases = new Map((greekData[kind] || []).map(function (item) { return [catalogKey(item), item]; }));
        data[kind] = (data[kind] || []).map(function (item) {
          var alias = aliases.get(catalogKey(item));
          return alias ? Object.assign({}, item, { gt: alias.t, gty: alias.ty || "", gh: alias.u.split("/").pop() }) : item;
        });
      });
    }
    this.data = data;
    this.productDocs = buildDocs(data.products, [["ty", 5], ["t", 4], ["tg", 1.5], ["gt", 4], ["gty", 5]]);
    this.collectionDocs = buildDocs(data.collections, [["t", 4], ["gt", 4]]);
    this.articleDocs = buildDocs(data.articles, [["t", 4], ["gt", 4]]);
    // vocabulary for "did you mean": product type + title words, stored with
    // their REAL spelling; matching runs on the folded projection.
    var seen = new Set();
    var vocab = [];
    data.products.forEach(function (p) {
      splitWords((p.ty || "") + " " + (p.t || "") + " " + (p.gt || "") + " " + (p.gty || "")).forEach(function (w) {
        if (w.length < 3 || seen.has(w)) return;
        seen.add(w);
        vocab.push({ o: w, f: fold(w) });
      });
    });
    this.vocab = vocab;
    this.ready = true;
  };

  function fetchIndex(url, storage) {
    var cacheKey = "luxSearchIdx:v5:" + url;
    if (storage) {
      try {
        var cached = storage.getItem(cacheKey);
        if (cached) {
          var saved = JSON.parse(cached);
          if (saved.at > Date.now() - 30 * 60 * 1000 && Array.isArray(saved.data.products)) return Promise.resolve(saved.data);
        }
      } catch (e) { /* quota/parse: fall through to network */ }
    }
    return fetch(url)
      .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(function (page1) {
        var extra = [];
        for (var p = 2; p <= (page1.pages || 1); p++) {
          extra.push(fetch(url + (url.indexOf("?") === -1 ? "?" : "&") + "page=" + p).then(function (r) {
            if (!r.ok) throw new Error(r.status);
            return r.json();
          }));
        }
        return Promise.all(extra).then(function (pages) {
          pages.forEach(function (pg) { page1.products = page1.products.concat(pg.products); });
          return page1;
        });
      })
      .then(function (data) {
        if (storage) {
          try { storage.setItem(cacheKey, JSON.stringify({ at: Date.now(), data: data })); } catch (e) { /* quota: skip cache */ }
        }
        return data;
      });
  }

  LuxSearchEngine.prototype.load = function () {
    if (this.loading) return this.loading;
    var self = this;
    this.loading = fetchIndex(this.indexUrl, this.storage).then(function (data) {
      self.setData(data); // Native-language search stays usable even if aliases fail.
      if (data.locale === "el" || !data.locale) return self;
      var greekUrl = self.indexUrl.replace(/\/(?:en|ru|he)(?=\/collections\/)/, "");
      if (greekUrl === self.indexUrl) return self;
      return fetchIndex(greekUrl, self.storage).then(function (greekData) {
        self.setData(data, greekData);
        return self;
      }).catch(function () { return self; });
    })
      .catch(function (err) {
        self.failed = true;
        throw err;
      });
    return this.loading;
  };

  LuxSearchEngine.prototype.correct = function (rawTokens, tokens) {
    // Rebuild the query out of catalog vocabulary when a token only fuzzy-matched.
    // Distance 0 through a transliterated variant also counts: it swaps the raw
    // Latin token for its Greek catalog form (keeps corrected terms one-script).
    var changed = false;
    var self = this;
    var out = rawTokens.map(function (raw, i) {
      var variants = tokens[i].variants;
      var bestWord = null, bestD = 3;
      for (var v = 0; v < self.vocab.length; v++) {
        var w = self.vocab[v];
        for (var k = 0; k < variants.length; k++) {
          var tv = variants[k];
          if (tv.length < 2) continue;
          var max = Math.min(bestD - 1, tv.length < 4 ? 0 : tv.length >= 7 ? 2 : 1);
          if (max < 0) break;
          var d = editDist(w.f, tv, max);
          if (d < bestD) { bestD = d; bestWord = w.o; }
        }
        if (bestD === 0) break;
      }
      if (bestWord && bestWord !== raw) { changed = true; return bestWord; }
      return raw;
    });
    return changed ? out.join(" ") : null;
  };

  LuxSearchEngine.prototype.search = function (query) {
    if (!this.ready) return null;
    var rawTokens = splitWords(query);
    if (!rawTokens.length) return { products: [], collections: [], articles: [], corrected: null, terms: "" };
    var tokens = rawTokens.map(function (t) { return { raw: t, variants: tokenVariants(t) }; });

    var products = rank(this.productDocs, tokens, { limit: 6, rel: 0.55, min: 4, product: true });
    var collections = rank(this.collectionDocs, tokens, { limit: 4, rel: 0.55, min: 4, collection: true });
    var articles = rank(this.articleDocs, tokens, { limit: 3, rel: 0.55, min: 3 });

    // "did you mean": the top product hit needed fuzzy for some token, or nothing hit at all
    var corrected = null;
    var needsCorrection = !products.length || products[0].kinds.indexOf("fuzzy") !== -1;
    if (needsCorrection) {
      corrected = this.correct(rawTokens, tokens);
      if (corrected && corrected !== rawTokens.join(" ")) {
        var cTokens = splitWords(corrected).map(function (t) { return { raw: t, variants: tokenVariants(t) }; });
        var cProducts = rank(this.productDocs, cTokens, { limit: 6, rel: 0.55, min: 4, product: true });
        if (cProducts.length && (!products.length || cProducts[0].score > products[0].score)) {
          products = cProducts;
          collections = rank(this.collectionDocs, cTokens, { limit: 4, rel: 0.55, min: 4, collection: true });
          articles = rank(this.articleDocs, cTokens, { limit: 3, rel: 0.55, min: 3 });
        } else {
          corrected = null;
        }
      } else {
        corrected = null;
      }
    }

    // Greeklish carry: the form submit lands on Shopify's FULL search, which
    // only honors exact indexed words (measured: tag "stroma" does not answer
    // q=stromata). So the raw term rides only when it exists verbatim on the
    // top hit; otherwise carry the vocabulary's real-spelling form. No blind
    // transliteration fallback — it writes ο for ω, η for ι and dead-ends.
    if (!corrected && products.length) {
      var top = products[0].item;
      var topWords = splitWords((top.ty || "") + " " + (top.t || "") + " " + (top.gt || "") + " " + (top.gty || ""));
      var rawExplained = rawTokens.every(function (t) { return topWords.indexOf(t) !== -1; });
      if (!rawExplained) {
        var carried = this.correct(rawTokens, tokens);
        if (carried && carried !== rawTokens.join(" ")) corrected = carried;
      }
    }

    if (corrected) corrected = displayTerms(corrected);
    var primaryCollection = null;
    if (tokens.length === 1 && rawTokens[0].length >= 4) {
      CATEGORY_PARENTS.some(function (entry) {
        var hit = bestWordScore(fold(entry[0]), tokens[0].variants, 1);
        if (!hit || hit.kind === "sub") return false;
        var parent = this.data.collections.find(function (c) {
          return (c.gh || c.u.split("/").pop()) === entry[1];
        });
        if (!parent) return false;
        primaryCollection = parent;
        return true;
      }, this);
    }
    if (collections.length && rawTokens.every(function (t) { return t.length >= 3; })) {
      var candidate = collections[0];
      var wholeCategory = [candidate.item.t, candidate.item.gt || ""].some(function (title) {
        var words = splitWords(title).map(fold);
        return words.length === tokens.length && coverage(words, tokens) === 1;
      });
      if (!primaryCollection && wholeCategory && candidate.kinds.every(function (k) {
        return k === "exact" || k === "prefix" || k === "stem";
      })) primaryCollection = candidate.item;
    }
    if (primaryCollection) {
      collections = [{ item: primaryCollection }].concat(collections.filter(function (c) {
        return c.item.u !== primaryCollection.u;
      })).slice(0, 4);
    }
    return {
      products: products.map(function (s) { return s.item; }),
      collections: collections.map(function (s) { return s.item; }),
      articles: articles.map(function (s) { return s.item; }),
      corrected: corrected,
      terms: corrected || rawTokens.join(" "),
      primaryCollection: primaryCollection,
    };
  };

  // Inline completion ("predict what they'll write"): the shortest localized
  // collection title that starts with the typed text. Same-script titles
  // complete in their own letters; a Latin (greeklish) query completes against
  // the LATIN PROJECTION of Greek titles, so the ghost stays in the shopper's
  // alphabet ("goniako" → "goniakoi kanapedes") and scoring translates later.
  LuxSearchEngine.prototype.complete = function (query) {
    if (!this.ready) return null;
    var nq = norm(query);
    if (nq.length < 2 || !/[\p{L}]$/u.test(query)) return null;
    var latinQuery = !/[^\u0000-ɏ]/.test(nq);
    var fq = fold(nq);
    var best = null, bestText = null, bestLen = Infinity;
    for (var i = 0; i < this.data.collections.length; i++) {
      var c = this.data.collections[i];
      var nt = norm(c.t);
      var candidate = null;
      if (nt.length === c.t.length && fold(nt).indexOf(fq) === 0 && c.t.length > query.length) {
        // same-script prefix (fold-tolerant): remainder from the original title
        candidate = query + c.t.slice(query.length);
      } else if (latinQuery) {
        var lat = greekToLatin(nt);
        if (lat !== nt && fold(lat).indexOf(fq) === 0 && lat.length > nq.length) {
          candidate = query + lat.slice(nq.length);
        }
      }
      if (!candidate) continue;
      var candLen = norm(candidate).length;
      // shortest completion wins; product count breaks ties (general beats niche)
      if (candLen < bestLen || (candLen === bestLen && (c.n || 0) > (best.n || 0))) {
        best = c; bestText = candidate; bestLen = candLen;
      }
    }
    if (!best) return null;
    return { text: bestText, url: best.u };
  };

  // expose: browser global + CommonJS for node unit tests
  var api = { LuxSearchEngine: LuxSearchEngine, _internals: { norm: norm, fold: fold, splitWords: splitWords, greeklish: greeklish, greekToLatin: greekToLatin, editDist: editDist } };
  if (typeof module !== "undefined" && module.exports) module.exports = api;
  root.LuxSearch = api;
})(typeof window !== "undefined" ? window : globalThis);
